import { z } from 'zod';
import { idSchema, storageLocationSchema, unitKeySchema } from './common.js';
/** Pantry payloads (Phase 1). Routes mount at /api/v1/pantry. */
export const expirationSourceSchema = z.enum(['ingredient', 'category', 'manual', 'unknown']);
export const pantryItemDtoSchema = z.object({
    id: idSchema,
    ingredientId: idSchema,
    ingredientName: z.string(),
    productId: idSchema.nullable(),
    qty: z.number(),
    unit: unitKeySchema,
    /** Restock threshold in `unit`: below it, list generation adds a `restock` line. Null = untracked. */
    minQty: z.number().nullable(),
    location: storageLocationSchema,
    /** ISO date (YYYY-MM-DD); null = unknown. */
    expiresOn: z.iso.date().nullable(),
    /** Only set on create responses (how `expiresOn` was determined). */
    expiresOnSource: expirationSourceSchema.optional(),
    /** Negative = already expired; null when `expiresOn` is null. */
    daysUntilExpiry: z.number().int().nullable(),
    purchasedAt: z.iso.datetime().nullable(),
    storeId: idSchema.nullable(),
    openedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const pantryListQuerySchema = z.object({
    location: storageLocationSchema.optional(),
    /** Only items expiring within N days (includes already-expired). */
    expiringWithinDays: z.coerce.number().int().min(0).max(3650).optional(),
    /** Case-insensitive substring match on the ingredient name. */
    q: z.string().trim().max(120).optional(),
});
export const pantryGroupSchema = z.object({
    location: storageLocationSchema,
    items: z.array(pantryItemDtoSchema),
});
/** `items` is sorted by location (pantry, fridge, freezer), then soonest expiry (unknown last), then name. */
export const pantryListResponseSchema = z.object({
    items: z.array(pantryItemDtoSchema),
    groups: z.array(pantryGroupSchema),
});
export const createPantryItemRequestSchema = z
    .object({
    ingredientId: idSchema.optional(),
    /** Free-text ingredient name; created in the shared catalog if unknown. */
    name: z.string().trim().min(1).max(120).optional(),
    qty: z.number().positive().max(1_000_000),
    unit: unitKeySchema,
    /** Keep this much on hand: below it, generation adds a `restock` line (PLAN §4.3). */
    minQty: z.number().positive().max(1_000_000).optional(),
    location: storageLocationSchema.default('pantry'),
    /** Omit to have it estimated from the ingredient's shelf life. */
    expiresOn: z.iso.date().optional(),
    purchasedAt: z.iso.datetime().optional(),
    storeId: idSchema.optional(),
    productId: idSchema.optional(),
})
    .refine((v) => Boolean(v.ingredientId) !== Boolean(v.name), {
    message: 'Provide exactly one of ingredientId or name',
    path: ['ingredientId'],
});
export const updatePantryItemRequestSchema = z
    .object({
    qty: z.number().positive().max(1_000_000).optional(),
    unit: unitKeySchema.optional(),
    /** null stops tracking the staple. */
    minQty: z.number().positive().max(1_000_000).nullable().optional(),
    location: storageLocationSchema.optional(),
    expiresOn: z.iso.date().nullable().optional(),
    openedAt: z.iso.datetime().nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const consumePantryItemRequestSchema = z.object({
    qty: z.number().positive().max(1_000_000),
    /** Defaults to the item's unit. */
    unit: unitKeySchema.optional(),
});
export const consumePantryItemResponseSchema = z.object({
    /** null when the item was fully consumed and removed. */
    item: pantryItemDtoSchema.nullable(),
    removed: z.boolean(),
});
export const pantryExpiringQuerySchema = z.object({
    days: z.coerce.number().int().min(0).max(3650).default(3),
});
export const pantryExpiringResponseSchema = z.object({
    days: z.number().int(),
    items: z.array(pantryItemDtoSchema),
});
//# sourceMappingURL=pantry.js.map